'use client';

import { useSearchParams } from 'next/navigation';
import { useCallback, useLayoutEffect, useRef, useState, useEffect } from 'react';
import { Card } from '@/components/Card';
import type { StatsByRound } from '@/lib/query';
import { VotesByRound } from './charts/VotesByRound';
import { CumulativeScore } from './charts/CummulativeScore';
import { RoundInfo } from './RoundInfo';
import styles from './RoundPerformance.module.css';
import { Tabs } from '@/components/Tabs';

export const RoundPerformance = ({
  statsByRound,
}: {
  statsByRound: StatsByRound;
}) => {
  const searchParams = useSearchParams();
  const view = searchParams.get('view') ?? 'votes';
  const round = searchParams.get('round');

  let roundNumber;
  if (round) {
    try {
      roundNumber = parseInt(round);
    } catch {
      roundNumber = statsByRound.length;
    }
  } else {
    roundNumber = statsByRound.length;
  }

  const selectedRoundStats = statsByRound[roundNumber - 1];

  const setRound = useCallback((roundNumber: number) => {
    const url = new URL(window.location.href);
    url.searchParams.set('round', String(roundNumber + 1));
    window.history.replaceState(null, '', url.toString());
  }, []);

  const setView = useCallback((view: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('view', view);
    window.history.replaceState(null, '', url.toString());
  }, []);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);

  useLayoutEffect(() => {
    if (chartContainerRef.current) {
      setChartWidth(chartContainerRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (chartContainerRef.current) {
        setChartWidth(chartContainerRef.current.clientWidth);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Card className={styles.roundPerformance}>
      <div className={styles.chartContainer}>
        <div className={styles.chartHeader}>
          <h3 className={styles.title}>
            Performance by round
          </h3>
          <Tabs
            value={view}
            onChange={setView}
            tabs={[
              {
                label: 'Vote Spread',
                value: 'votes',
              },
              {
                label: 'Cummulative Score',
                value: 'cummulative',
              },
            ]}
          />
        </div>
        <div
          ref={chartContainerRef}
          className={styles.chart}
        >
          {view === 'cummulative' ? (
            <CumulativeScore
              statsByRound={statsByRound}
              onSetRound={setRound}
              isMobile={chartWidth < 768}
            />
          ) : (
            <VotesByRound
              statsByRound={statsByRound}
              onSetRound={setRound}
              isMobile={chartWidth < 768}
            />
          )}
        </div>
      </div>
      <div className={styles.roundInfoContainer}>
        {selectedRoundStats && <RoundInfo round={selectedRoundStats} />}
      </div>
    </Card>
  );
};
